import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/argus/AppShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Radar, X, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";

const STATUS_STYLES = {
  completed: "text-safe",
  running: "text-gold",
  queued: "text-ink/60",
  failed: "text-sev-critical",
  cancelled: "text-ink/40",
};

export default function Scans() {
  const [scans, setScans] = useState([]);
  const [projects, setProjects] = useState({});
  const [status, setStatus] = useState("all");
  const [scanner, setScanner] = useState("all");
  const [project, setProject] = useState("all");

  const refresh = async () => {
    const params = {};
    if (status !== "all") params.status_filter = status;
    if (scanner !== "all") params.scanner = scanner;
    if (project !== "all") params.project_id = project;
    const [s, p] = await Promise.all([
      api.get("/scans", { params }),
      api.get("/projects"),
    ]);
    setScans(s.data);
    setProjects(Object.fromEntries(p.data.map((x) => [x.id, x])));
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, scanner, project]);

  const hasFilters = status !== "all" || scanner !== "all" || project !== "all";

  const downloadScanPdf = async (scan) => {
    try {
      const token = localStorage.getItem("argus.token");
      const r = await fetch(`${API}/scans/${scan.id}/report.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 402) {
        const body = await r.json().catch(() => ({}));
        toast.error(body.detail || "Pro/Enterprise feature.");
        return;
      }
      if (!r.ok) throw new Error("download failed");
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u;
      a.download = `argus-scan-${scan.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(u);
      toast.success("Scan report downloaded.");
    } catch (e) {
      toast.error("Couldn't generate the scan report.");
    }
  };

  return (
    <AppShell title="Scans">
      <div className="flex flex-wrap gap-3 mb-5" data-testid="scans-filters">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 w-40 bg-paper border-line" data-testid="scans-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={scanner} onValueChange={setScanner}>
          <SelectTrigger className="h-10 w-36 bg-paper border-line" data-testid="scans-scanner-filter"><SelectValue placeholder="Scanner" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scanners</SelectItem>
            <SelectItem value="sast">SAST</SelectItem>
            <SelectItem value="dast">DAST</SelectItem>
            <SelectItem value="sca">SCA</SelectItem>
          </SelectContent>
        </Select>
        <Select value={project} onValueChange={setProject}>
          <SelectTrigger className="h-10 w-56 bg-paper border-line" data-testid="scans-project-filter"><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {Object.values(projects).map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="outline" onClick={() => { setStatus("all"); setScanner("all"); setProject("all"); }}
            className="h-10 border-line bg-transparent text-ink/70" data-testid="scans-clear-filters">
            <X size={14} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      {scans.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-12 text-center" data-testid="scans-empty">
          <Radar size={28} className="mx-auto text-ink/40 mb-3" />
          <p className="text-ink/70 mb-4">{hasFilters ? "No scans match these filters." : "No scans yet. Run your first against a demo project."}</p>
          <Link to="/app/projects" className="text-gold">Go to projects →</Link>
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-panel overflow-hidden">
          <table className="w-full text-sm" data-testid="scans-table">
            <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Scan</th>
                <th className="text-left px-4 py-2.5">Project</th>
                <th className="text-left px-4 py-2.5">Scanner</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Findings</th>
                <th className="text-left px-4 py-2.5">Started</th>
                <th className="text-left px-4 py-2.5">PDF</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((s) => (
                <React.Fragment key={s.id}>
                <tr className="border-t border-line dense-row" data-testid={`scan-row-${s.id}`}>
                  <td className="px-4 font-mono text-xs">{s.id.slice(0, 8)}</td>
                  <td className="px-4">
                    <Link to={`/app/projects/${s.project_id}`} className="hover:text-gold">
                      {projects[s.project_id]?.name || s.project_id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-4 uppercase font-semibold text-xs">{s.scanner}</td>
                  <td className="px-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${STATUS_STYLES[s.status] || ""}`}>{s.status}</span>
                      {(s.status === "running" || s.status === "queued") && (
                        <div className="h-1.5 w-24 bg-raised rounded-full overflow-hidden">
                          <div className="h-full bg-gold transition-all" style={{ width: `${s.progress}%` }} />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4">{s.findings_count ?? 0}</td>
                  <td className="px-4 text-xs text-ink/60 font-mono">{new Date(s.started_at).toLocaleString()}</td>
                  <td className="px-4">
                    {s.status === "completed" ? (
                      <button
                        onClick={() => downloadScanPdf(s)}
                        className="inline-flex items-center gap-1 text-xs text-gold hover:underline"
                        title="Download per-scan PDF (Pro/Enterprise)"
                        data-testid={`scan-pdf-${s.id}`}
                      >
                        <FileDown size={12} /> PDF
                      </button>
                    ) : (
                      <span className="text-ink/30 text-xs">—</span>
                    )}
                  </td>
                </tr>
                {Array.isArray(s.engine_summary) && s.engine_summary.length > 0 && (
                  <tr className="border-t border-line/40" data-testid={`scan-engines-${s.id}`}>
                    <td colSpan={7} className="px-4 py-2 bg-paper/30">
                      <div className="flex flex-wrap gap-2 items-center text-[11px]">
                        <span className="uppercase tracking-wider text-ink/40 mr-2">Engines</span>
                        {s.engine_summary.map((e) => {
                          const cls = {
                            running:  "border-gold/50 text-gold animate-pulse",
                            pulling:  "border-gold/50 text-gold animate-pulse",
                            scanning: "border-gold/50 text-gold animate-pulse",
                            writing:  "border-gold/50 text-gold animate-pulse",
                            ok:       "border-safe/50 text-safe",
                            failed:   "border-sev-critical/50 text-sev-critical",
                            skipped:  "border-line text-ink/40",
                            queued:   "border-line text-ink/50",
                          }[e.status] || "border-line text-ink/50";
                          const live = ["running", "pulling", "scanning", "writing"].includes(e.status);
                          const layerInfo = (e.status === "pulling" && (e.layers_seen || e.layers_total))
                            ? ` (layer ${e.layers_seen || 0}${e.layers_total ? ` / ${e.layers_total}` : ""})`
                            : "";
                          return (
                            <span key={e.engine_id} className={`inline-flex items-center gap-1.5 border ${cls} rounded-full px-2 py-0.5 font-mono`} data-testid={`engine-pill-${s.id}-${e.engine_id}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${e.status === "ok" ? "bg-safe" : e.status === "failed" ? "bg-sev-critical" : live ? "bg-gold" : "bg-ink/30"}`} />
                              {e.engine_id}
                              {live && <span className="text-ink/60 italic">· {e.status}{layerInfo}</span>}
                              {e.status === "ok" && <span className="text-ink/50">· {e.count}</span>}
                              {e.status === "skipped" && <span className="text-ink/40 italic">· skipped</span>}
                              {e.status === "failed" && <span className="text-sev-critical/80 italic" title={e.skipped_reason || "failed"}>· failed</span>}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
